import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'motion/react';
import {
  Palette,
  ArrowLeft,
  CheckCircle,
  Download,
  Save,
  Eye,
  Play,
  Share,
  Loader2,
  Wand2,
  Presentation,
  Image as ImageIcon,
  Layout,
  Type,
  Sparkles } from
'lucide-react';

interface Slide {
  id: string;
  title: string;
  content: string;
  type: string;
  notes: string;
  imageKeywords: string;
  interactiveElement?: string;
}

interface DesignApplicatorProps {
  presentationData: any;
  updatePresentationData: (updates: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const DesignApplicator: React.FC<DesignApplicatorProps> = ({
  presentationData,
  updatePresentationData,
  onPrevious,
  isLoading,
  setIsLoading
}) => {
  const [designProgress, setDesignProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('idle'); // idle, applying, completed
  const [finalPresentation, setFinalPresentation] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentSlidePreview, setCurrentSlidePreview] = useState(0);

  const { toast } = useToast();
  const { user } = useAuth();

  const slides: Slide[] = presentationData.approvedContent || presentationData.content || [];
  const template = presentationData.template;

  // Default template values to prevent undefined errors
  const defaultTemplate = {
    id: 'modern',
    name: 'Modern',
    colorScheme: {
      primary: '#2563eb',
      secondary: '#7c3aed',
      background: '#ffffff',
      accent: '#06b6d4'
    },
    typography: 'modern'
  };

  const safeTemplate = template ? {
    ...defaultTemplate,
    ...template,
    colorScheme: {
      ...defaultTemplate.colorScheme,
      ...(template.colorScheme || {})
    }
  } : defaultTemplate;

  useEffect(() => {
    // Auto-start design application when component mounts
    if (slides.length > 0 && currentStep === 'idle') {
      applyDesign();
    }
  }, [slides]);

  // Helper function to safely convert content to string and count words
  const getWordCount = (text: any): number => {
    if (!text) return 0;
    // Convert to string if it's not already, handle arrays and objects
    let stringContent = '';
    if (typeof text === 'string') {
      stringContent = text;
    } else if (Array.isArray(text)) {
      stringContent = text.join(' ');
    } else if (typeof text === 'object') {
      stringContent = JSON.stringify(text);
    } else {
      stringContent = String(text);
    }

    // Count words by splitting on whitespace and filtering empty strings
    return stringContent.trim().split(/\s+/).filter((word) => word.length > 0).length;
  };

  const applyDesign = async () => {
    if (!slides || slides.length === 0) {
      toast({
        title: "Missing Content",
        description: "No slides found to apply design to.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setCurrentStep('applying');
    setDesignProgress(10);

    try {
      // Simulate design application process
      const steps = [
      { progress: 20, message: "Applying color scheme..." },
      { progress: 40, message: "Formatting slide layouts..." },
      { progress: 60, message: "Adding visual elements..." },
      { progress: 80, message: "Optimizing typography..." },
      { progress: 90, message: "Finalizing design..." },
      { progress: 100, message: "Design complete!" }];


      for (const step of steps) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setDesignProgress(step.progress);

        // You can add actual design processing logic here
        console.log(step.message);
      }

      // Create final presentation with applied design - with proper null checks
      const designedSlides = slides.map((slide, index) => {
        if (!slide) {
          console.warn(`Slide at index ${index} is undefined, using default values`);
          return {
            id: `slide-${index}`,
            title: `Slide ${index + 1}`,
            content: 'No content available',
            type: 'content',
            notes: '',
            imageKeywords: '',
            design: {
              backgroundColor: safeTemplate.colorScheme.background,
              primaryColor: safeTemplate.colorScheme.primary,
              secondaryColor: safeTemplate.colorScheme.secondary,
              accentColor: safeTemplate.colorScheme.accent,
              layout: 'standard',
              typography: safeTemplate.typography,
              slideNumber: index + 1,
              totalSlides: slides.length
            },
            imageUrl: generateImagePlaceholder('education'),
            appliedAt: new Date().toISOString()
          };
        }

        return {
          ...slide,
          design: {
            backgroundColor: safeTemplate.colorScheme.background,
            primaryColor: safeTemplate.colorScheme.primary,
            secondaryColor: safeTemplate.colorScheme.secondary,
            accentColor: safeTemplate.colorScheme.accent,
            layout: getLayoutForSlideType(slide.type || 'content'),
            typography: safeTemplate.typography,
            slideNumber: index + 1,
            totalSlides: slides.length
          },
          imageUrl: generateImagePlaceholder(slide.imageKeywords || 'education'),
          appliedAt: new Date().toISOString()
        };
      });

      // Calculate word count safely
      const totalWordCount = slides.reduce((total, slide) => {
        if (!slide) return total;
        const titleWords = getWordCount(slide.title);
        const contentWords = getWordCount(slide.content);
        return total + titleWords + contentWords;
      }, 0);

      const presentation = {
        ...presentationData,
        slides: designedSlides,
        template: safeTemplate,
        designAppliedAt: new Date().toISOString(),
        isComplete: true,
        metadata: {
          totalSlides: slides.length,
          estimatedDuration: slides.length * 2,
          wordCount: totalWordCount,
          template: safeTemplate.name,
          generatedBy: user?.Name || 'Unknown'
        }
      };

      setFinalPresentation(presentation);
      setCurrentStep('completed');

      updatePresentationData({
        finalPresentation: presentation,
        isComplete: true
      });

      toast({
        title: "Design Applied!",
        description: `Your presentation "${presentationData.title}" is ready with the ${safeTemplate.name} design.`
      });

    } catch (error) {
      console.error('Error applying design:', error);
      setCurrentStep('idle');
      toast({
        title: "Design Failed",
        description: "Failed to apply design to presentation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getLayoutForSlideType = (type: string) => {
    const layouts = {
      title: 'centered',
      content: 'standard',
      list: 'bullets',
      image: 'image-focus',
      quote: 'centered-quote',
      comparison: 'two-column',
      conclusion: 'centered'
    };
    return layouts[type] || 'standard';
  };

  const generateImagePlaceholder = (keywords: string) => {
    // Generate unsplash URL based on keywords
    const keywordArray = (keywords || 'education').split(',').map((k) => k.trim());
    const primaryKeyword = keywordArray[0] || 'education';
    return `https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop&q=80`;
  };

  const handleSavePresentation = async () => {
    if (!finalPresentation || !user?.ID) return;

    setIsSaving(true);
    try {
      const presentationData = {
        teacher_id: user.ID,
        title: finalPresentation.title || 'Untitled Presentation',
        description: finalPresentation.description || `A presentation about ${finalPresentation.topic}`,
        subject: finalPresentation.subject || '',
        grade_level: finalPresentation.gradeLevel || '',
        slides_data: JSON.stringify(finalPresentation.slides),
        template_theme: finalPresentation.template.id,
        total_slides: finalPresentation.slides.length,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await window.ezsite.apis.tableCreate(15255, presentationData);

      if (error) throw error;

      toast({
        title: "Presentation Saved!",
        description: `"${presentationData.title}" has been saved successfully.`
      });

    } catch (error) {
      console.error('Error saving presentation:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save presentation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPresentation = async () => {
    if (!finalPresentation) return;

    try {
      toast({
        title: "Exporting Presentation",
        description: "Generating PDF export..."
      });

      // Dynamic import of jsPDF
      const { jsPDF } = await import('jspdf');

      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < finalPresentation.slides.length; i++) {
        const slide = finalPresentation.slides[i];
        if (i > 0) pdf.addPage();

        // Apply template colors (simulate design) with safe defaults
        const bgColor = safeTemplate.colorScheme.background || '#ffffff';
        const primaryColor = safeTemplate.colorScheme.primary || '#000000';

        // Add background color
        pdf.setFillColor(bgColor);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        // Add title
        pdf.setFontSize(28);
        pdf.setTextColor(primaryColor);
        pdf.setFont('helvetica', 'bold');
        const titleLines = pdf.splitTextToSize(slide.title || `Slide ${i + 1}`, pageWidth - 40);
        pdf.text(titleLines, 20, 40);

        // Add content - handle different content types safely
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        let contentText = 'No content available';

        if (slide.content) {
          if (typeof slide.content === 'string') {
            contentText = slide.content;
          } else if (Array.isArray(slide.content)) {
            contentText = slide.content.join('\n');
          } else {
            contentText = String(slide.content);
          }
        }

        const contentLines = pdf.splitTextToSize(contentText, pageWidth - 40);
        pdf.text(contentLines, 20, 70);

        // Add slide number
        pdf.setFontSize(10);
        pdf.setTextColor('#666666');
        pdf.text(`${i + 1} / ${finalPresentation.slides.length}`, pageWidth - 30, pageHeight - 10);

        // Add template branding
        pdf.setFontSize(8);
        pdf.text(`Template: ${safeTemplate.name}`, 20, pageHeight - 10);
      }

      pdf.save(`${finalPresentation.title || 'presentation'}.pdf`);

      toast({
        title: "Export Complete!",
        description: "Presentation has been exported as PDF."
      });

    } catch (error) {
      console.error('Error exporting presentation:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export presentation. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (!slides || slides.length === 0) {
    return (
      <div className="text-center py-12" data-id="mrtgiw13e" data-path="src/components/ai/DesignApplicator.tsx">
        <div className="max-w-md mx-auto" data-id="3izhlbcg0" data-path="src/components/ai/DesignApplicator.tsx">
          <Palette className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="5kgqcl5fg" data-path="src/components/ai/DesignApplicator.tsx" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="83kgzzntj" data-path="src/components/ai/DesignApplicator.tsx">No Content Found</h3>
          <p className="text-gray-600 mb-6" data-id="r5xin6jjv" data-path="src/components/ai/DesignApplicator.tsx">
            Go back to previous steps to create and approve content first.
          </p>
          <Button onClick={onPrevious} variant="outline" data-id="p2aikb81d" data-path="src/components/ai/DesignApplicator.tsx">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="xquzpw5a0" data-path="src/components/ai/DesignApplicator.tsx" />
            Back to Content Review
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6" data-id="72h79ix5h" data-path="src/components/ai/DesignApplicator.tsx">
      {/* Header */}
      <div className="text-center" data-id="9gvpzzsho" data-path="src/components/ai/DesignApplicator.tsx">
        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="2ip6uqyw9" data-path="src/components/ai/DesignApplicator.tsx">
          Generating Final Presentation
        </h3>
        <p className="text-gray-600" data-id="r425g5hk1" data-path="src/components/ai/DesignApplicator.tsx">
          Applying {safeTemplate.name} template to your {slides.length} slides
        </p>
      </div>

      {/* Design Progress */}
      {currentStep === 'applying' &&
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto" data-id="1yv15iz9f" data-path="src/components/ai/DesignApplicator.tsx">

          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50" data-id="ww7gtgn9e" data-path="src/components/ai/DesignApplicator.tsx">
            <CardHeader data-id="f1q5kh6oc" data-path="src/components/ai/DesignApplicator.tsx">
              <CardTitle className="flex items-center gap-2 text-blue-900" data-id="cnkkw5lxy" data-path="src/components/ai/DesignApplicator.tsx">
                <Wand2 className="h-6 w-6 animate-pulse" data-id="n29fqhh1z" data-path="src/components/ai/DesignApplicator.tsx" />
                Applying Design with AI
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="7zjm2e4u2" data-path="src/components/ai/DesignApplicator.tsx">
              <div className="flex items-center justify-between text-sm" data-id="0zfci5c6p" data-path="src/components/ai/DesignApplicator.tsx">
                <span className="text-blue-700" data-id="s6rahr2n7" data-path="src/components/ai/DesignApplicator.tsx">Progress</span>
                <span className="text-blue-600 font-medium" data-id="p9oceb25p" data-path="src/components/ai/DesignApplicator.tsx">{designProgress}%</span>
              </div>
              <Progress value={designProgress} className="h-3" data-id="bmo00c3vu" data-path="src/components/ai/DesignApplicator.tsx" />
              <div className="flex items-center gap-2 text-sm text-blue-700" data-id="50jyo977j" data-path="src/components/ai/DesignApplicator.tsx">
                <Loader2 className="h-4 w-4 animate-spin" data-id="0qlfkdmhy" data-path="src/components/ai/DesignApplicator.tsx" />
                <span data-id="zdsnilms8" data-path="src/components/ai/DesignApplicator.tsx">Creating professional slides with {safeTemplate.name} template...</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      }

      {/* Completion Status */}
      {currentStep === 'completed' && finalPresentation &&
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-4xl mx-auto" data-id="a3y1j8k35" data-path="src/components/ai/DesignApplicator.tsx">

          <Card className="border-green-200 bg-green-50" data-id="2x6123b60" data-path="src/components/ai/DesignApplicator.tsx">
            <CardHeader data-id="w2bpnikw8" data-path="src/components/ai/DesignApplicator.tsx">
              <div className="flex items-center justify-between" data-id="4hm6tqm80" data-path="src/components/ai/DesignApplicator.tsx">
                <div className="flex items-center gap-3" data-id="gwmgquuwz" data-path="src/components/ai/DesignApplicator.tsx">
                  <CheckCircle className="h-8 w-8 text-green-600" data-id="av8u78jxt" data-path="src/components/ai/DesignApplicator.tsx" />
                  <div data-id="scbg43y1e" data-path="src/components/ai/DesignApplicator.tsx">
                    <CardTitle className="text-green-900" data-id="vw6ilwnze" data-path="src/components/ai/DesignApplicator.tsx">Presentation Complete!</CardTitle>
                    <p className="text-green-700 text-sm" data-id="4jsvn5bvf" data-path="src/components/ai/DesignApplicator.tsx">
                      Your presentation is ready with the {safeTemplate.name} design
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="f64aerop4" data-path="src/components/ai/DesignApplicator.tsx">
                  <Button onClick={handleSavePresentation} disabled={isSaving} size="sm" data-id="94998c9d9" data-path="src/components/ai/DesignApplicator.tsx">
                    {isSaving ?
                  <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="yn7k1zgi5" data-path="src/components/ai/DesignApplicator.tsx" />
                        Saving...
                      </> :

                  <>
                        <Save className="h-4 w-4 mr-2" data-id="81yw62kpx" data-path="src/components/ai/DesignApplicator.tsx" />
                        Save
                      </>
                  }
                  </Button>
                  <Button onClick={handleExportPresentation} variant="outline" size="sm" data-id="wwejpgxty" data-path="src/components/ai/DesignApplicator.tsx">
                    <Download className="h-4 w-4 mr-2" data-id="dtygm90n2" data-path="src/components/ai/DesignApplicator.tsx" />
                    Export PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Presentation Preview */}
          <div className="mt-6 space-y-4" data-id="i8oihw7uc" data-path="src/components/ai/DesignApplicator.tsx">
            <Card data-id="thdzatrwi" data-path="src/components/ai/DesignApplicator.tsx">
              <CardHeader data-id="zrsaxh9um" data-path="src/components/ai/DesignApplicator.tsx">
                <div className="flex items-center justify-between" data-id="l1paatqdo" data-path="src/components/ai/DesignApplicator.tsx">
                  <CardTitle className="flex items-center gap-2" data-id="hm5hkqteb" data-path="src/components/ai/DesignApplicator.tsx">
                    <Eye className="h-5 w-5 text-blue-600" data-id="5hxnotpeh" data-path="src/components/ai/DesignApplicator.tsx" />
                    Presentation Preview
                  </CardTitle>
                  <div className="flex items-center gap-2" data-id="37yc5i7v1" data-path="src/components/ai/DesignApplicator.tsx">
                    <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentSlidePreview(Math.max(0, currentSlidePreview - 1))}
                    disabled={currentSlidePreview === 0} data-id="pvoeyuwl2" data-path="src/components/ai/DesignApplicator.tsx">

                      ←
                    </Button>
                    <Badge variant="outline" data-id="z2orz4fx0" data-path="src/components/ai/DesignApplicator.tsx">
                      {currentSlidePreview + 1} of {finalPresentation.slides.length}
                    </Badge>
                    <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentSlidePreview(Math.min(finalPresentation.slides.length - 1, currentSlidePreview + 1))}
                    disabled={currentSlidePreview === finalPresentation.slides.length - 1} data-id="1e37o8wz9" data-path="src/components/ai/DesignApplicator.tsx">

                      →
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent data-id="uppxwa4za" data-path="src/components/ai/DesignApplicator.tsx">
                <AnimatePresence mode="wait" data-id="l4j9clofz" data-path="src/components/ai/DesignApplicator.tsx">
                  <motion.div
                  key={currentSlidePreview}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="aspect-video bg-gradient-to-br rounded-lg overflow-hidden shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${safeTemplate.colorScheme.primary}10, ${safeTemplate.colorScheme.secondary}10)`
                  }} data-id="7szmtsijl" data-path="src/components/ai/DesignApplicator.tsx">

                    <div className="h-full p-8 flex flex-col justify-center" data-id="ohg11nbzs" data-path="src/components/ai/DesignApplicator.tsx">
                      <div className="mb-4" data-id="04zsf47s4" data-path="src/components/ai/DesignApplicator.tsx">
                        <Badge
                        variant="secondary"
                        style={{ backgroundColor: safeTemplate.colorScheme.accent + '20', color: safeTemplate.colorScheme.accent }} data-id="9ig059lqe" data-path="src/components/ai/DesignApplicator.tsx">

                          {finalPresentation.slides[currentSlidePreview].type}
                        </Badge>
                      </div>
                      <h2
                      className="text-3xl font-bold mb-6"
                      style={{ color: safeTemplate.colorScheme.primary }} data-id="44gywc6ty" data-path="src/components/ai/DesignApplicator.tsx">

                        {finalPresentation.slides[currentSlidePreview].title}
                      </h2>
                      <div className="prose prose-lg max-w-none" data-id="06zohryri" data-path="src/components/ai/DesignApplicator.tsx">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap" data-id="l892mxyiv" data-path="src/components/ai/DesignApplicator.tsx">
                          {typeof finalPresentation.slides[currentSlidePreview].content === 'string' ?
                        finalPresentation.slides[currentSlidePreview].content :
                        JSON.stringify(finalPresentation.slides[currentSlidePreview].content)}
                        </p>
                      </div>
                      <div className="mt-auto pt-6 flex justify-between items-center text-sm text-gray-500" data-id="13doh4drc" data-path="src/components/ai/DesignApplicator.tsx">
                        <span data-id="mznin63d2" data-path="src/components/ai/DesignApplicator.tsx">{finalPresentation.title}</span>
                        <span data-id="sm7dytxsk" data-path="src/components/ai/DesignApplicator.tsx">{currentSlidePreview + 1} / {finalPresentation.slides.length}</span>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Presentation Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="pmf9b1j1w" data-path="src/components/ai/DesignApplicator.tsx">
              <Card data-id="2ygt3uwqp" data-path="src/components/ai/DesignApplicator.tsx">
                <CardContent className="pt-6" data-id="6h4bva1s0" data-path="src/components/ai/DesignApplicator.tsx">
                  <div className="flex items-center gap-3" data-id="lbnr1m9gs" data-path="src/components/ai/DesignApplicator.tsx">
                    <div className="p-2 bg-blue-100 rounded-lg" data-id="qay7sb7it" data-path="src/components/ai/DesignApplicator.tsx">
                      <Presentation className="h-5 w-5 text-blue-600" data-id="1hludzdz6" data-path="src/components/ai/DesignApplicator.tsx" />
                    </div>
                    <div data-id="camf6aikd" data-path="src/components/ai/DesignApplicator.tsx">
                      <p className="text-sm text-gray-600" data-id="wyjscud5q" data-path="src/components/ai/DesignApplicator.tsx">Total Slides</p>
                      <p className="text-2xl font-bold text-blue-600" data-id="tlrkchvig" data-path="src/components/ai/DesignApplicator.tsx">{finalPresentation.slides.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="xwe5uaj1u" data-path="src/components/ai/DesignApplicator.tsx">
                <CardContent className="pt-6" data-id="ta9u92atv" data-path="src/components/ai/DesignApplicator.tsx">
                  <div className="flex items-center gap-3" data-id="xzmekehid" data-path="src/components/ai/DesignApplicator.tsx">
                    <div className="p-2 bg-green-100 rounded-lg" data-id="c8kt3jez5" data-path="src/components/ai/DesignApplicator.tsx">
                      <Layout className="h-5 w-5 text-green-600" data-id="kp4rmfjq1" data-path="src/components/ai/DesignApplicator.tsx" />
                    </div>
                    <div data-id="tq59hbr9d" data-path="src/components/ai/DesignApplicator.tsx">
                      <p className="text-sm text-gray-600" data-id="6sxjiemry" data-path="src/components/ai/DesignApplicator.tsx">Template</p>
                      <p className="text-lg font-bold text-green-600" data-id="srw7177bx" data-path="src/components/ai/DesignApplicator.tsx">{safeTemplate.name}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="6ubb0515q" data-path="src/components/ai/DesignApplicator.tsx">
                <CardContent className="pt-6" data-id="k666k0fso" data-path="src/components/ai/DesignApplicator.tsx">
                  <div className="flex items-center gap-3" data-id="o2suuewly" data-path="src/components/ai/DesignApplicator.tsx">
                    <div className="p-2 bg-purple-100 rounded-lg" data-id="si93ots2u" data-path="src/components/ai/DesignApplicator.tsx">
                      <Type className="h-5 w-5 text-purple-600" data-id="8p7p3sxbm" data-path="src/components/ai/DesignApplicator.tsx" />
                    </div>
                    <div data-id="46hmyioq0" data-path="src/components/ai/DesignApplicator.tsx">
                      <p className="text-sm text-gray-600" data-id="p2jjsbwea" data-path="src/components/ai/DesignApplicator.tsx">Words</p>
                      <p className="text-2xl font-bold text-purple-600" data-id="lg9i306pv" data-path="src/components/ai/DesignApplicator.tsx">{finalPresentation.metadata.wordCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="nfo8xiykk" data-path="src/components/ai/DesignApplicator.tsx">
                <CardContent className="pt-6" data-id="s3tjsw1z0" data-path="src/components/ai/DesignApplicator.tsx">
                  <div className="flex items-center gap-3" data-id="kvlbexv7q" data-path="src/components/ai/DesignApplicator.tsx">
                    <div className="p-2 bg-orange-100 rounded-lg" data-id="dkpl02two" data-path="src/components/ai/DesignApplicator.tsx">
                      <Sparkles className="h-5 w-5 text-orange-600" data-id="cm1m84l31" data-path="src/components/ai/DesignApplicator.tsx" />
                    </div>
                    <div data-id="6meppf59s" data-path="src/components/ai/DesignApplicator.tsx">
                      <p className="text-sm text-gray-600" data-id="esnnmhlqd" data-path="src/components/ai/DesignApplicator.tsx">Est. Time</p>
                      <p className="text-2xl font-bold text-orange-600" data-id="l756qqk3j" data-path="src/components/ai/DesignApplicator.tsx">{finalPresentation.metadata.estimatedDuration}m</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* All Slides Grid */}
            <Card data-id="5k3w0obpn" data-path="src/components/ai/DesignApplicator.tsx">
              <CardHeader data-id="8ds4fhvmy" data-path="src/components/ai/DesignApplicator.tsx">
                <CardTitle data-id="agexhel9k" data-path="src/components/ai/DesignApplicator.tsx">All Slides Overview</CardTitle>
              </CardHeader>
              <CardContent data-id="xoksze91k" data-path="src/components/ai/DesignApplicator.tsx">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="nv9rtx8aq" data-path="src/components/ai/DesignApplicator.tsx">
                  {finalPresentation.slides.map((slide, index) =>
                <div
                  key={slide.id}
                  className={`aspect-video border rounded-lg cursor-pointer transition-all hover:shadow-lg p-3 ${
                  currentSlidePreview === index ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`
                  }
                  onClick={() => setCurrentSlidePreview(index)}
                  style={{
                    borderColor: currentSlidePreview === index ? safeTemplate.colorScheme.primary : '#e5e7eb'
                  }} data-id="45ltyei0s" data-path="src/components/ai/DesignApplicator.tsx">

                      <div className="h-full flex flex-col" data-id="x4xrtqrye" data-path="src/components/ai/DesignApplicator.tsx">
                        <div className="flex items-center justify-between mb-2" data-id="zexntibor" data-path="src/components/ai/DesignApplicator.tsx">
                          <Badge variant="outline" className="text-xs" data-id="38yxzgqdm" data-path="src/components/ai/DesignApplicator.tsx">
                            {index + 1}
                          </Badge>
                          <Badge
                        variant="secondary"
                        className="text-xs"
                        style={{ backgroundColor: safeTemplate.colorScheme.primary + '20' }} data-id="nh2rqb8je" data-path="src/components/ai/DesignApplicator.tsx">

                            {slide.type}
                          </Badge>
                        </div>
                        <h5 className="text-xs font-medium line-clamp-2 mb-1" data-id="wqn1irlji" data-path="src/components/ai/DesignApplicator.tsx">
                          {slide.title}
                        </h5>
                        <p className="text-xs text-gray-600 line-clamp-3 flex-1" data-id="5711vaovc" data-path="src/components/ai/DesignApplicator.tsx">
                          {typeof slide.content === 'string' ? slide.content : JSON.stringify(slide.content)}
                        </p>
                      </div>
                    </div>
                )}
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      }

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-6 border-t" data-id="hkrqh5rz0" data-path="src/components/ai/DesignApplicator.tsx">
        <Button onClick={onPrevious} variant="outline" data-id="qaomrt7im" data-path="src/components/ai/DesignApplicator.tsx">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="makaed9ul" data-path="src/components/ai/DesignApplicator.tsx" />
          Back to Review
        </Button>

        <div className="flex items-center gap-3" data-id="lu6bq39gg" data-path="src/components/ai/DesignApplicator.tsx">
          {currentStep === 'completed' &&
          <>
              <Button onClick={handleExportPresentation} variant="outline" data-id="zjieoy1j3" data-path="src/components/ai/DesignApplicator.tsx">
                <Download className="h-4 w-4 mr-2" data-id="o61nbvu51" data-path="src/components/ai/DesignApplicator.tsx" />
                Export PDF
              </Button>
              <Button onClick={handleSavePresentation} disabled={isSaving} data-id="opt21tky7" data-path="src/components/ai/DesignApplicator.tsx">
                {isSaving ?
              <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="7kob7s42t" data-path="src/components/ai/DesignApplicator.tsx" />
                    Saving...
                  </> :

              <>
                    <Save className="h-4 w-4 mr-2" data-id="4wae8axru" data-path="src/components/ai/DesignApplicator.tsx" />
                    Save Presentation
                  </>
              }
              </Button>
            </>
          }
        </div>
      </div>
    </div>);

};

export default DesignApplicator;